import { useId, type ChangeEvent, type FC } from 'react';
import { typography } from '@mui/system';
import { useTranslation } from 'react-i18next';

import { callDetailsExtensionColumnWidth } from 'features/call/call-details/styles';
import { sanitizePhoneExtension } from 'features/call/call-hearing/helpers';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

export type CallDetailsExtensionInputWidth = 'full' | 'column' | 'sixty';

export interface CallDetailsExtensionInputProps {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  width?: CallDetailsExtensionInputWidth;
}

const extensionInputWidth = (width: CallDetailsExtensionInputWidth): string => {
  switch (width) {
    case 'full':
      return '100%';
    case 'sixty':
      return '60%';
    case 'column':
      return callDetailsExtensionColumnWidth;
    default: {
      const exhaustiveCheck: never = width;
      throw new Error(`Unhandled extension input width: ${exhaustiveCheck}`);
    }
  }
};

export const CallDetailsExtensionInput: FC<CallDetailsExtensionInputProps> = ({
  value,
  onChange,
  disabled = false,
  width = 'column',
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'callDetails' });
  const inputId = useId();
  const label = t(disabled ? 'extension' : 'enterExtension');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(sanitizePhoneExtension(event.target.value));
  };

  return (
    <Container $width={width}>
      <StyledLabel htmlFor={inputId}>{label}</StyledLabel>
      <StyledInput
        id={inputId}
        name='hearingPhoneExtension'
        data-testid='call-details-extension-input'
        inputMode='numeric'
        autoComplete='off'
        disabled={disabled}
        value={value}
        onChange={handleChange}
      />
    </Container>
  );
};

const StyledLabel = styled('label')`
  color: ${theme.palette.grey[100]};
  display: inline-block;
  ${typography(theme.typography.label)};
`;

const Container = styled('div')<{ $width: CallDetailsExtensionInputWidth }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(6)};
  width: ${({ $width }) => extensionInputWidth($width)};
  max-width: 100%;
  box-sizing: border-box;
  ${({ $width }) => ($width === 'full' ? '' : 'align-self: flex-start;')}
`;

const StyledInput = styled('input')`
  ${typography(theme.typography.body1)};
  display: block;
  width: 100%;
  max-width: 100%;
  padding: ${theme.spacing(4, 6)};
  color: ${theme.palette.common.black};
  background-color: ${theme.palette.common.white};
  border: none;
  border-radius: ${theme.spacing(4)};
  outline: none;
  box-sizing: border-box;

  &:disabled {
    color: ${theme.palette.grey[50]};
    background-color: ${theme.palette.grey[800]};
    border: 1px solid ${theme.palette.grey[600]};
    opacity: 0.7;
  }
`;
